// InfoPopover — words behind a TAP TARGET (ADR-080 §12 · COMPONENTS §Overlay
// patterns): nothing lives only in a tooltip. A tooltip repeats what the screen
// says (an icon's name); where the words MATTER — a sentence of help, a list a
// cell folds ("+2"), a breakdown behind a glyph strip — they sit behind a
// visible target that opens on click AND on tap: the "i" button by default, or
// the caller's own inline trigger (the "+2", "3 others", the strip). The panel
// is the family's `Popover` — a small card on a desk, the sheet from the bottom
// on a phone — so one mechanism carries every such fact to every screen. The
// trigger is a real button: the keyboard reaches it, and a list row around it
// does not open on the click (the table ignores clicks on its controls).
//
// The rule it holds: three words or fewer sit ON the screen (a caption);
// anything longer, or a list, sits HERE. A hover-only `Tooltip` stays for an
// icon's name alone — `Tooltip.tripwire.test.ts` names the files that may hold one.

import type { ReactNode } from 'react';
import { icons } from './icons.js';
import { IconButton } from './IconButton.js';
import { Popover, type PopoverPlacement } from './Popover.js';

export interface InfoPopoverProps {
  /** The panel's accessible name — and the "i" button's ("More about the tolerance"). */
  label: string;
  /** The words — text, or structure (a list, sections). */
  children: ReactNode;
  /** The caller's own visible trigger ("+2" · "3 others" · a glyph strip), rendered inside a text button; the "i" when absent. */
  trigger?: ReactNode;
  /** Classes on the inline trigger's button (a dotted underline on a text trigger). */
  triggerClassName?: string;
  /** Default `bottom start`. */
  placement?: PopoverPlacement;
  /** Forces the panel's face (stories). */
  face?: 'desk' | 'phone';
}

export function InfoPopover({ label, children, trigger, triggerClassName = '', placement = 'bottom start', face }: InfoPopoverProps) {
  return (
    <Popover
      label={label}
      placement={placement}
      face={face}
      renderTrigger={() =>
        trigger === undefined ? (
          // `-my-2` keeps the 40 px hit square from inflating the line it sits on.
          <IconButton variant="ghost" aria-label={label} className="-my-2">
            <icons.help size={16} aria-hidden className="text-[var(--color-text-secondary)]" />
          </IconButton>
        ) : (
          // The 24 px floor (SC 2.5.8) — `-my-1` keeps it from inflating the line it sits on.
          <button
            type="button"
            className={`-my-1 inline-flex min-h-6 min-w-6 items-center justify-center rounded outline-none focus-ring ${triggerClassName}`}
          >
            {trigger}
          </button>
        )
      }
    >
      <div className="max-w-xs p-3 text-sm text-[var(--color-text-primary)]">{children}</div>
    </Popover>
  );
}
